import { useState, type ClipboardEvent } from 'react';
import { InputWrapper } from '@repletoryreach/sdk-ui/inputs';
import { useT } from '../i18n/I18nProvider';
import './words-input.css';

/**
 * A short list of words or phrases, one chip each (1 November 2026, first for a language's change, greeting, yes
 * and no words and its Meta codes on the Languages page; the customer console has the same control). Enter or a
 * comma adds what was typed, Backspace in an empty box takes the last chip off, and a pasted list is split on commas
 * and new lines. Built on the design system's `InputWrapper`, so the label sits above, and the hint and any error sit
 * under the box with `aria-describedby` wired.
 */
export function WordsInput({
  label, description, error, value, onChange, max, maxLength = 40, lowercase = false, disabled = false, placeholder, ...rest
}: {
  label: string;
  description?: string;
  error?: string;
  value: string[];
  onChange: (words: string[]) => void;
  /** No more chips than this; the box says so once it is full. */
  max?: number;
  /** The longest word or phrase, as the server keeps it. */
  maxLength?: number;
  /** Store the words in lower case, as the server does, so what is shown is what is saved. */
  lowercase?: boolean;
  disabled?: boolean;
  placeholder?: string;
  'data-testid'?: string;
}) {
  const t = useT();
  const [draft, setDraft] = useState('');
  const full = max !== undefined && value.length >= max;

  function add(raw: string | string[]) {
    const next = [...value];
    for (const piece of Array.isArray(raw) ? raw : [raw]) {
      let word = piece.trim().replace(/\s+/g, ' ').slice(0, maxLength);
      if (lowercase) word = word.toLowerCase();
      if (!word || next.some((w) => w.toLowerCase() === word.toLowerCase())) continue;
      if (max !== undefined && next.length >= max) break;
      next.push(word);
    }
    if (next.length !== value.length) onChange(next);
    setDraft('');
  }

  function paste(e: ClipboardEvent<HTMLInputElement>) {
    const text = e.clipboardData.getData('text');
    if (!/[,\n]/.test(text)) return;
    e.preventDefault();
    add(text.split(/[,\n]/));
  }

  return (
    <InputWrapper label={label} description={description} error={error} data-testid={rest['data-testid']}>
      {({ inputId, describedBy, invalid, className }) => (
        <div className={`${className} rr-words__box`} data-disabled={disabled || undefined} data-invalid={invalid || undefined}>
          {value.map((word) => (
            <span key={word} className="rr-words__chip">
              <span className="rr-words__text">{word}</span>
              {!disabled && (
                <button
                  type="button"
                  className="rr-words__remove"
                  aria-label={t('common.words.remove', { word })}
                  onClick={() => onChange(value.filter((w) => w !== word))}
                >
                  ×
                </button>
              )}
            </span>
          ))}
          <input
            id={inputId}
            className="rr-words__input"
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
            value={draft}
            maxLength={maxLength}
            disabled={disabled || full}
            placeholder={full ? t('common.words.full', { max: max ?? 0 }) : placeholder ?? t('common.words.add')}
            onChange={(e) => setDraft(e.currentTarget.value)}
            onPaste={paste}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(draft); }
              else if (e.key === 'Backspace' && !draft && value.length > 0) onChange(value.slice(0, -1));
            }}
            onBlur={() => add(draft)}
          />
        </div>
      )}
    </InputWrapper>
  );
}
